import { useEffect } from "react";

/**
 * Lightweight per-page SEO hook. Updates the document title + meta tags
 * (description, OG, Twitter, canonical) on mount. Restores on unmount so
 * SPA navigation between pages doesn't leak stale metadata.
 *
 * Useful for JS-aware crawlers (Googlebot, modern preview tools) that DO
 * execute JS but only read meta from the rendered DOM.
 */
function setMetaTag(attr, name, content) {
  if (!content) return null;
  let tag = document.querySelector(`meta[${attr}="${name}"]`);
  let prev = null;
  if (tag) {
    prev = tag.getAttribute("content");
    tag.setAttribute("content", content);
  } else {
    tag = document.createElement("meta");
    tag.setAttribute(attr, name);
    tag.setAttribute("content", content);
    document.head.appendChild(tag);
  }
  return { tag, prev };
}

function setCanonical(href) {
  if (!href) return null;
  let link = document.querySelector('link[rel="canonical"]');
  let prev = null;
  if (link) {
    prev = link.getAttribute("href");
    link.setAttribute("href", href);
  } else {
    link = document.createElement("link");
    link.setAttribute("rel", "canonical");
    link.setAttribute("href", href);
    document.head.appendChild(link);
  }
  return { link, prev };
}

export function useSeo({ title, description, ogTitle, ogDescription, ogImage, canonical, ogType = "website", noindex = false, noindexFollow = false, jsonLd = null }) {
  useEffect(() => {
    const prevTitle = document.title;
    if (title) document.title = title;

    const updates = [];
    if (description) updates.push(setMetaTag("name", "description", description));
    if (ogTitle || title) updates.push(setMetaTag("property", "og:title", ogTitle || title));
    if (ogDescription || description) updates.push(setMetaTag("property", "og:description", ogDescription || description));
    // v3.99c.8 — social crawlers (Facebook, X, LinkedIn, Discord) require
    // ABSOLUTE og:image URLs. Promote any leading "/" path to a thecannaforge.com
    // URL so previews work no matter which surface the link is shared on.
    const absoluteOgImage = ogImage && ogImage.startsWith("/") ? `https://thecannaforge.com${ogImage}` : ogImage;
    if (absoluteOgImage) updates.push(setMetaTag("property", "og:image", absoluteOgImage));
    if (ogType) updates.push(setMetaTag("property", "og:type", ogType));
    if (ogTitle || title) updates.push(setMetaTag("name", "twitter:title", ogTitle || title));
    if (ogDescription || description) updates.push(setMetaTag("name", "twitter:description", ogDescription || description));
    if (absoluteOgImage) updates.push(setMetaTag("name", "twitter:image", absoluteOgImage));
    if (absoluteOgImage) updates.push(setMetaTag("name", "twitter:card", "summary_large_image"));

    // v3.76 — `noindex` flag drives a `<meta name="robots" content="noindex,nofollow">`
    // tag while the page is mounted. Used by the Bank Approval Mode hardening
    // layer so marketplace-y routes (/community, /drops, /auctions, /raffles,
    // /vendor/*, breeder onboarding) drop out of crawler indices while
    // processor review is in progress, and re-enter as soon as BAM flips off.
    //
    // v3.99c.20 — `noindexFollow` switches to `noindex,follow` for landing
    // pages that are temporarily thin (e.g. /seeds/autoflower with 0 entries)
    // but should still allow crawlers to follow internal links to the rest
    // of the entity graph. Backend /api/seo/render emits the same value.
    const robotsValue = noindex
      ? (noindexFollow ? "noindex,follow" : "noindex,nofollow")
      : null;
    if (robotsValue) {
      updates.push(setMetaTag("name", "robots", robotsValue));
    } else if (canonical) {
      // v3.99c.40 — When a page explicitly declares it IS indexable
      // (noindex=false) but the index.html SSR bootstrap previously wrote
      // a `noindex,*` robots tag (via /api/seo/meta), strip it so the
      // React-side truth wins. Only acts when the existing meta is a
      // noindex variant so we don't clobber correct values.
      const existing = document.querySelector('meta[name="robots"]');
      if (existing && /noindex/i.test(existing.getAttribute("content") || "")) {
        existing.setAttribute("content", "index, follow, max-image-preview:large");
        updates.push({ tag: existing, prev: existing.getAttribute("content") });
      }
    }

    let canon = null;
    if (canonical) {
      const url = canonical.startsWith("http") ? canonical : `https://thecannaforge.com${canonical}`;
      canon = setCanonical(url);
      updates.push(setMetaTag("property", "og:url", url));
    }

    // v3.99bq — Structured-data block (JSON-LD). Inject one `data-managed`
    // <script> per mounted page so route changes don't accumulate.
    let ldNode = null;
    if (jsonLd) {
      // Remove any previous page-managed block first.
      document.querySelectorAll('script[data-managed="page"]').forEach((n) => n.remove());
      ldNode = document.createElement("script");
      ldNode.type = "application/ld+json";
      ldNode.dataset.managed = "page";
      try {
        ldNode.textContent = JSON.stringify(jsonLd);
      } catch {
        ldNode.textContent = "{}";
      }
      document.head.appendChild(ldNode);
    }

    return () => {
      document.title = prevTitle;
      updates.forEach((u) => {
        if (u && u.tag && u.prev !== null) u.tag.setAttribute("content", u.prev);
      });
      // robots meta added on a page where there was none before should be
      // removed on unmount so next page renders with default indexability.
      if (robotsValue) {
        const robots = document.querySelector('meta[name="robots"]');
        if (robots && robots.getAttribute("content") === robotsValue) {
          robots.remove();
        }
      }
      if (canon && canon.link && canon.prev !== null) canon.link.setAttribute("href", canon.prev);
      if (ldNode && ldNode.parentNode) ldNode.parentNode.removeChild(ldNode);
    };
  }, [title, description, ogTitle, ogDescription, ogImage, canonical, ogType, noindex, noindexFollow, jsonLd]);
}

/**
 * useBamNoIndex — emits a `<meta name="robots" content="noindex,nofollow">`
 * tag while Bank Approval Mode is ON, used on routes whose existence is
 * marketplace-flavored (community / drops / auctions / raffles / vendor / breeder
 * onboarding). The moment BAM flips off, the meta is removed and the page
 * resumes normal indexing on the next mount.
 */
export function useBamNoIndex(bankApprovalMode) {
  useEffect(() => {
    if (!bankApprovalMode) return undefined;
    const existing = document.querySelector('meta[name="robots"]');
    const previous = existing ? existing.getAttribute("content") : null;
    if (existing) {
      existing.setAttribute("content", "noindex,nofollow");
    } else {
      const tag = document.createElement("meta");
      tag.setAttribute("name", "robots");
      tag.setAttribute("content", "noindex,nofollow");
      document.head.appendChild(tag);
    }
    return () => {
      const robots = document.querySelector('meta[name="robots"]');
      if (!robots) return;
      if (previous === null) {
        robots.remove();
      } else {
        robots.setAttribute("content", previous);
      }
    };
  }, [bankApprovalMode]);
}
